import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Spacer,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useMemo, useState } from "react";
import { fetchPPLData, patchPPLData } from "../../api/industryInsights";
import { Context, useCurrentProfile } from "../../ContextWrapper";
import CustomTableContainer from "../../theme/components/tableContainer";
import { customCurrencyFormat } from "../../utils";

export const InsightValuesBreakdown = ({ insight_id, turnBack }) => {
  const [loading, setLoading] = useState(true);
  const [pplData, setPPLData] = useState([]);
  const [focusedInputName, setFocusedInputName] = useState(null);
  const context = React.useContext(Context);
  const { profile } = useCurrentProfile();

  const performanceGroupMap = useMemo(
    () => ({
      average: " Average Performers",
      ten: " Top 10% Performers",
      one: " Top 1% Performers",
    }),
    []
  );
  const revenueGroupMap = useMemo(
    () => ({
      fiftyk: " Under $50k",
      hundredk: " $50k–$100k",
      onefiftyk: " $100k–$150k",
      twofiftyk: " $150k–$250k",
      overtwofiftyk: " Over $250k",
    }),
    []
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        fetchPPLData(insight_id)
          .then((data) => {
            setPPLData(data);
          })
          .catch((err) => {
            console.error("Failed to fetch PPL Data:", err);
          });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [insight_id]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const response = await patchPPLData(insight_id, pplData);

      setPPLData(response);

      setPPLData(response.data);

      context.fireToast(
        "Success",
        "Insight PPL updated successfully!",
        "success"
      );
    } catch (err) {
      console.error("There was an error updating:", err);
      context.fireToast(
        "Error",
        "An error occurred during the update",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePPLPLInputChange = (e, pplId) => {
    const { name, value } = e.target;

    setPPLData((prevList) => {
      return prevList.map((ppl) => {
        if (ppl.id !== pplId) return ppl;

        const updatedPPL = { ...ppl, [name]: value };

        const profit =
          100 -
          ((parseFloat(updatedPPL.cogs_pl) || 0) +
            (parseFloat(updatedPPL.marketing_pl) || 0) +
            (parseFloat(updatedPPL.team_pl) || 0) +
            (parseFloat(updatedPPL.practice_cost_pl) || 0) +
            (parseFloat(updatedPPL.overhead_pl) || 0) +
            (parseFloat(updatedPPL.business_development_pl) || 0) +
            (parseFloat(updatedPPL.taxes_pl) || 0));

        return {
          ...updatedPPL,
          profit_pl: String(profit !== 100 ? profit : 0),
        };
      });
    });
  };

  const handlePLInputChange = (e) => {
    const { name, value } = e.target;

    setPPLData((prev) => {
      const updatedData = { ...prev, [name]: value };

      const profit =
        100 -
        ((parseFloat(updatedData.cogs_pl) || 0) +
          (parseFloat(updatedData.marketing_pl) || 0) +
          (parseFloat(updatedData.team_pl) || 0) +
          (parseFloat(updatedData.practice_cost_pl) || 0) +
          (parseFloat(updatedData.overhead_pl) || 0) +
          (parseFloat(updatedData.business_development_pl) || 0) +
          (parseFloat(updatedData.taxes_pl) || 0));

      return { ...updatedData, profit_pl: String(profit) };
    });
  };

  const handleNormalInputChange = (e) => {
    const { name, value } = e.target;

    setPPLData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Stack textAlign={"center"} overflowX={"auto"} maxW={"100%"}>
      {loading && (
        <FontAwesomeIcon
          className="float-end fa-spin"
          icon={faSpinner}
          color="green"
        />
      )}
      <Stack align="center">
        <CustomTableContainer width={"100%"}>
          <form id="pplForm" onSubmit={(e) => handleSubmit(e)}>
            <Flex pb="5">
              <Box display={"flex"} alignItems="center" justifyContent="center">
                <Heading as="h3" size="md">
                  Performance Group
                  {performanceGroupMap[pplData.performance_group] ||
                    pplData.performance_group}{" "}
                  of revenue
                  {revenueGroupMap[pplData.revenue_group] ||
                    pplData.revenue_group}
                </Heading>
              </Box>
              <Spacer />
              <Box>
                <Stack direction="row" spacing={6} align="center">
                  {profile.type === "admin" && (
                    <Button type="submit" form="pplForm">
                      Save
                    </Button>
                  )}
                </Stack>
              </Box>
            </Flex>

            <Box
              maxHeight="calc(100vh - 350px)"
              bg="white"
              overflowY={"auto"}
              overflowX={"auto"}
            >
              <Table variant="unstyled" width={"100%"}>
                <Thead position="sticky" top={-1} zIndex={1}>
                  <Tr>
                    <Th textAlign={"center"}>Accounts</Th>
                    <Th textAlign={"center"}>P&L</Th>
                    <Th textAlign={"center"}>Values</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td textAlign={"left"} key={"labels"}>
                      <VStack align={"start"}>
                        <Box p={"9px 0px"}>Income</Box>
                        <Box p={"9px 0px"}>COGS</Box>
                        <Box p={"9px 0px"}>Marketing</Box>
                        <Box p={"9px 0px"}>Team</Box>
                        <Box p={"9px 0px"}>Practice Cost</Box>
                        <Box p={"9px 0px"}>Overhead</Box>
                        <Box p={"9px 0px"}>Business Development</Box>
                        <Box p={"9px 0px"}>Taxes</Box>
                        <Box p={"9px 0px"}>Net Profit</Box>
                      </VStack>
                    </Td>
                    <Td key={pplData.id}>
                      <VStack>
                        <Box
                          p={0}
                          height={"40px"}
                          display={"flex"}
                          alignItems={"center"}
                        >
                          -
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="cogs_pl"
                            value={
                              focusedInputName === "cogs_pl"
                                ? pplData.cogs_pl
                                : customCurrencyFormat(
                                    pplData.cogs_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() => setFocusedInputName("cogs_pl")}
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="marketing_pl"
                            value={
                              focusedInputName === "marketing_pl"
                                ? pplData.marketing_pl
                                : customCurrencyFormat(
                                    pplData.marketing_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() => setFocusedInputName("marketing_pl")}
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="team_pl"
                            value={
                              focusedInputName === "team_pl"
                                ? pplData.team_pl
                                : customCurrencyFormat(
                                    pplData.team_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() => setFocusedInputName("team_pl")}
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="practice_cost_pl"
                            value={
                              focusedInputName === "practice_cost_pl"
                                ? pplData.practice_cost_pl
                                : customCurrencyFormat(
                                    pplData.practice_cost_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() =>
                              setFocusedInputName("practice_cost_pl")
                            }
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="overhead_pl"
                            value={
                              focusedInputName === "overhead_pl"
                                ? pplData.overhead_pl
                                : customCurrencyFormat(
                                    pplData.overhead_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() => setFocusedInputName("overhead_pl")}
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="business_development_pl"
                            value={
                              focusedInputName === "business_development_pl"
                                ? pplData.business_development_pl
                                : customCurrencyFormat(
                                    pplData.business_development_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() =>
                              setFocusedInputName("business_development_pl")
                            }
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="taxes_pl"
                            value={
                              focusedInputName === "taxes_pl"
                                ? pplData.taxes_pl
                                : customCurrencyFormat(
                                    pplData.taxes_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            onFocus={() => setFocusedInputName("taxes_pl")}
                            onBlur={() => setFocusedInputName(null)}
                            onChange={(e) => handlePLInputChange(e)}
                          />
                        </Box>
                        <Box p={0}>
                          <Input
                            width={"75px"}
                            name="profit_pl"
                            border={"none"}
                            value={
                              focusedInputName === "profit_pl"
                                ? pplData.profit_pl
                                : customCurrencyFormat(
                                    pplData.profit_pl,
                                    true,
                                    false,
                                    true
                                  )
                            }
                            isReadOnly
                          />
                        </Box>
                      </VStack>
                    </Td>
                    <PPLRow
                      ppl={pplData}
                      handleInputChange={handleNormalInputChange}
                      handlePLInputChange={handlePPLPLInputChange}
                    />
                  </Tr>
                </Tbody>
              </Table>
            </Box>
          </form>
          <Stack align={"start"}>
            <Button
              width={"300px"}
              m={"10px 0px 0px 0px"}
              onClick={() => turnBack(false)}
            >
              Back to Insight Select
            </Button>
          </Stack>
        </CustomTableContainer>
      </Stack>
    </Stack>
  );
};

const PPLRow = ({ ppl, handleInputChange }) => {
  const [focusedInputName, setFocusedInputName] = useState(null);

  return (
    <Td key={ppl.id}>
      <VStack alignItems={"center"}>
        <Box>
          <Input
            width={"150px"}
            textAlign={"center"}
            name="income_forecast"
            value={
              focusedInputName === "income_forecast"
                ? ppl.income_forecast
                : customCurrencyFormat(
                    ppl.income_forecast,
                    false,
                    false,
                    true,
                    true
                  )
            }
            onFocus={() => setFocusedInputName("income_forecast")}
            onBlur={() => setFocusedInputName(null)}
            onChange={(e) => handleInputChange(e, ppl.id)}
          />
        </Box>
        <Box
          p={"0px 0px"}
          height={"40px"}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text>
            {customCurrencyFormat(ppl.cogs_forecast, false, false, true, true)}
          </Text>
        </Box>
        <Box
          p={"0px 0px"}
          height={"40px"}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text>
            {customCurrencyFormat(
              ppl.marketing_forecast,
              false,
              false,
              true,
              true
            )}
          </Text>
        </Box>
        <Box
          p={"0px 0px"}
          height={"40px"}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text>
            {customCurrencyFormat(ppl.team_forecast, false, false, true, true)}
          </Text>
        </Box>
        <Box
          p={"0px 0px"}
          height={"40px"}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text>
            {customCurrencyFormat(
              ppl.practice_cost_forecast,
              false,
              false,
              true,
              true
            )}
          </Text>
        </Box>
        <Box
          p={"0px 0px"}
          height={"40px"}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text>
            {customCurrencyFormat(
              ppl.overhead_forecast,
              false,
              false,
              true,
              true
            )}
          </Text>
        </Box>
        <Box
          p={"0px 0px"}
          height={"40px"}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text>
            {customCurrencyFormat(
              ppl.business_development_forecast,
              false,
              false,
              true,
              true
            )}
          </Text>
        </Box>
        <Box
          p={"0px 0px"}
          height={"40px"}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text>
            {customCurrencyFormat(ppl.taxes_forecast, false, false, true, true)}
          </Text>
        </Box>
        <Box
          p={"0px 0px"}
          height={"40px"}
          textAlign={"center"}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {customCurrencyFormat(
            ppl.net_operating_profit_forecast,
            false,
            false,
            true,
            true
          )}
        </Box>
      </VStack>
    </Td>
  );
};
